import { useId, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useSWRConfig } from 'swr';

import { CAPTURE_MODES, isLiabilityAccountType, type CaptureMode } from '@pfo/domain';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { formatVnd } from '../../../shared/lib/money.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { MoneyInput } from '../../../shared/ui/MoneyInput.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { setDefaultAccount } from '../../preferences/data/preferencesRepository.js';
import { PREFERENCES_KEY } from '../../preferences/hooks/usePreferences.js';
import {
  postOpeningBalance,
  setAccountActive,
  updateAccount,
  updateOpeningBalance,
  type Account,
  type StatementMonth,
} from '../data/accountsRepository.js';
import {
  accountStatementKey,
  openingEntryKey,
  useAccountStatement,
  useAccounts,
  useOpeningEntry,
} from '../hooks/useAccounts.js';
import {
  ACCOUNT_TYPE_LABELS,
  CAPTURE_MODE_LABELS,
  USAGE_SCOPE_LABELS,
  formatAccountBalance,
  formatStatementMonth,
  statementLabels,
} from '../model/accountLabels.js';
import styles from './AccountDetailPage.module.css';

/**
 * One account: what it holds now, what moved each month, and the few things that can be
 * changed about it.
 *
 * For a credit card the monthly block IS the statement the plan asks for — opening debt,
 * what was charged, what was paid, closing debt (G1/DBD-018). Paying part of a statement and
 * carrying the rest is normal, so nothing here expects a month to end at zero. Every figure
 * is computed by `v_account_monthly_statement` in SQL; this screen only formats.
 */
export function AccountDetailPage() {
  const { accountId } = useParams();
  const accountsState = useAccounts();
  const statementState = useAccountStatement(accountId ?? '');

  if (accountId === undefined) return <Navigate to="/tai-san" replace />;

  if (accountsState.status === 'loading') return <LoadingState label="Đang tải tài khoản…" />;
  if (accountsState.status === 'error') {
    return <ErrorState message={accountsState.message} onRetry={accountsState.retry} />;
  }

  const account = accountsState.overview.accounts.find((item) => item.id === accountId);
  if (!account) {
    return (
      <EmptyState
        icon="🔍"
        title="Không tìm thấy tài khoản"
        message="Tài khoản này không còn nữa hoặc không thuộc về bạn."
      >
        <Link to="/tai-san">Về danh sách tài khoản</Link>
      </EmptyState>
    );
  }

  const isLiability = isLiabilityAccountType(account.accountType);

  return (
    <div className={styles.page}>
      <p className={styles.breadcrumb}>
        <Link to="/tai-san">← Tài sản</Link>
      </p>

      <header className={styles.hero}>
        <h1 className={styles.name}>{account.name}</h1>
        <p className={styles.meta}>
          {ACCOUNT_TYPE_LABELS[account.accountType]}
          {account.institution ? ` · ${account.institution}` : ''} ·{' '}
          {USAGE_SCOPE_LABELS[account.usageScope]} · {CAPTURE_MODE_LABELS[account.captureMode]}
        </p>
        <p className={cx(styles.balance, isLiability && account.balance < 0 && styles.balanceDebt)}>
          {formatAccountBalance(account.balance, account.accountType)}
        </p>
        {!account.isActive ? <p className={lists.badge}>Đã đóng</p> : null}
      </header>

      <AccountActions
        key={account.id}
        account={account}
        onChanged={() => void accountsState.refresh()}
      />

      <Statement account={account} state={statementState} />
    </div>
  );
}

function AccountActions({ account, onChanged }: { account: Account; onChanged: () => void }) {
  const contextState = useCurrentContext();
  const { mutate } = useSWRConfig();
  const nameId = useId();
  const captureId = useId();
  const openingId = useId();

  const [name, setName] = useState(account.name);
  const [captureMode, setCaptureMode] = useState<CaptureMode>(account.captureMode);
  const [openingBalance, setOpeningBalance] = useState<number | null>(null);
  const openingState = useOpeningEntry(account.id, account.accountType);
  const opening = openingState.status === 'ready' ? openingState.opening : null;
  const [isConfirmingArchive, setConfirmingArchive] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();
  const isDirty = trimmed !== account.name || captureMode !== account.captureMode;
  const canSave = !busy && isDirty && trimmed !== '';

  /** Every action here is a confirmed write: run it, then reread what changed (skill 11.1). */
  async function run(action: () => Promise<void>, successMessage: string) {
    setBusy(true);
    setMessage(null);
    setNotice(null);
    try {
      await action();
      onChanged();
      await Promise.all([
        mutate(accountStatementKey(account.id)),
        mutate(openingEntryKey(account.id)),
        mutate(PREFERENCES_KEY),
      ]);
      setNotice(successMessage);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>Thiết lập tài khoản</h2>

      <div className={forms.field}>
        <label htmlFor={nameId}>Tên tài khoản</label>
        <input
          id={nameId}
          className={forms.input}
          type="text"
          value={name}
          enterKeyHint="done"
          autoComplete="off"
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className={forms.field}>
        <label htmlFor={captureId}>Cách ghi giao dịch</label>
        <select
          id={captureId}
          className={forms.input}
          value={captureMode}
          onChange={(event) => setCaptureMode(event.target.value as CaptureMode)}
        >
          {CAPTURE_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {CAPTURE_MODE_LABELS[mode]}
            </option>
          ))}
        </select>
        <p className={forms.hint}>
          Ví điện tử như MoMo chọn “Nhập tổng cuối tháng” để đối chiếu một lần mỗi tháng.
        </p>
      </div>

      <button
        type="button"
        className={forms.primary}
        disabled={!canSave}
        onClick={() =>
          void run(
            () => updateAccount(account.id, { name: trimmed, captureMode }),
            'Đã lưu thay đổi.',
          )
        }
      >
        Lưu thay đổi
      </button>

      {/* AJ1: the opening balance typed at creation can be corrected here — the same entry is
          rewritten on its original date (A7). A closed month refuses (MONTH_CLOSED). */}
      {opening !== null && contextState.status === 'ready' ? (
        <div className={forms.field}>
          <label htmlFor={openingId}>
            {isLiabilityAccountType(account.accountType)
              ? 'Số tiền còn nợ lúc bắt đầu'
              : 'Số dư đầu kỳ'}
          </label>
          <MoneyInput
            id={openingId}
            value={openingBalance ?? opening.amount}
            onChange={setOpeningBalance}
          />
          <p className={forms.hint}>
            Đang ghi {formatVnd(opening.amount)} vào ngày{' '}
            {new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short' }).format(
              new Date(opening.occurredAt),
            )}
            . Nhập nhầm lúc tạo tài khoản (ví dụ đã gồm lương tháng này) thì sửa ở đây; số dư và báo
            cáo tính lại theo số mới.
          </p>
          <button
            type="button"
            className={forms.secondary}
            disabled={
              busy ||
              openingBalance === null ||
              openingBalance <= 0 ||
              openingBalance === opening.amount
            }
            onClick={() =>
              void run(
                () =>
                  updateOpeningBalance(
                    {
                      householdId: contextState.context.household.id,
                      subjectId: contextState.context.selfSubject.id,
                    },
                    {
                      entryId: opening.entryId,
                      occurredAt: opening.occurredAt,
                      accountId: account.id,
                      accountType: account.accountType,
                      amount: openingBalance ?? 0,
                    },
                  ),
                'Đã sửa số dư đầu kỳ.',
              )
            }
          >
            Sửa số dư đầu kỳ
          </button>
        </div>
      ) : null}

      {/* The opening balance can only be recorded once, because a second one would silently
          double the account's starting money. It is offered while the account has no history
          at all — the recovery path when the entry failed right after the account was made. */}
      {opening === null && account.balance === 0 && contextState.status === 'ready' ? (
        <div className={forms.field}>
          <label htmlFor={openingId}>
            {isLiabilityAccountType(account.accountType) ? 'Số tiền còn nợ' : 'Số dư đầu kỳ'}
          </label>
          <MoneyInput id={openingId} value={openingBalance} onChange={setOpeningBalance} />
          <p className={forms.hint}>
            Ghi lại số dư thật lúc bắt đầu dùng ứng dụng. Sau đó số dư chỉ thay đổi qua giao dịch.
          </p>
          <button
            type="button"
            className={forms.secondary}
            disabled={busy || openingBalance === null || openingBalance === 0}
            onClick={() =>
              void run(
                () =>
                  postOpeningBalance(
                    {
                      householdId: contextState.context.household.id,
                      subjectId: contextState.context.selfSubject.id,
                    },
                    {
                      accountId: account.id,
                      accountType: account.accountType,
                      amount: openingBalance ?? 0,
                    },
                  ),
                'Đã ghi số dư đầu kỳ.',
              )
            }
          >
            Ghi số dư đầu kỳ
          </button>
        </div>
      ) : null}

      {!account.isDefault ? (
        <button
          type="button"
          className={forms.secondary}
          disabled={busy || !account.isActive}
          onClick={() =>
            void run(() => setDefaultAccount(account.id), 'Đã đặt làm tài khoản mặc định.')
          }
        >
          Đặt làm tài khoản mặc định
        </button>
      ) : (
        <p className={forms.hint}>Đây là tài khoản mặc định khi thêm giao dịch nhanh.</p>
      )}

      {account.isActive ? (
        isConfirmingArchive ? (
          <div className={forms.actions}>
            <button
              type="button"
              className={forms.secondary}
              onClick={() => setConfirmingArchive(false)}
            >
              Hủy
            </button>
            <button
              type="button"
              className={cx(forms.primary, styles.danger)}
              disabled={busy}
              onClick={() =>
                void run(() => setAccountActive(account.id, false), 'Đã đóng tài khoản.').then(() =>
                  setConfirmingArchive(false),
                )
              }
            >
              Đóng tài khoản
            </button>
          </div>
        ) : (
          // Closing an account is not one-tap (skill 13.2). Nothing is deleted either: the
          // account is hidden and its history stays in the ledger.
          <button
            type="button"
            className={cx(forms.secondary, styles.dangerOutline)}
            disabled={busy}
            onClick={() => setConfirmingArchive(true)}
          >
            Đóng tài khoản
          </button>
        )
      ) : (
        <button
          type="button"
          className={forms.secondary}
          disabled={busy}
          onClick={() => void run(() => setAccountActive(account.id, true), 'Đã mở lại tài khoản.')}
        >
          Mở lại tài khoản
        </button>
      )}

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}
    </section>
  );
}

function Statement({
  account,
  state,
}: {
  account: Account;
  state: ReturnType<typeof useAccountStatement>;
}) {
  const labels = statementLabels(account.accountType);

  if (state.status === 'loading') return <LoadingState label="Đang tải sao kê…" />;
  if (state.status === 'error') {
    return <ErrorState message={state.message} onRetry={state.retry} />;
  }

  if (state.months.length === 0) {
    return (
      <EmptyState
        icon="🧾"
        title="Chưa có giao dịch nào"
        message="Khi bạn ghi giao dịch cho tài khoản này, sao kê từng tháng sẽ hiện ở đây."
      />
    );
  }

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>Sao kê theo tháng</h2>
      {state.months.map((month) => (
        <MonthBlock key={month.periodStart} month={month} labels={labels} account={account} />
      ))}
    </section>
  );
}

function MonthBlock({
  month,
  labels,
  account,
}: {
  month: StatementMonth;
  labels: ReturnType<typeof statementLabels>;
  account: Account;
}) {
  const isLiability = isLiabilityAccountType(account.accountType);
  // On a liability the credit side adds to the debt and the debit side pays it off; on an
  // asset it is the other way round. Both numbers come from SQL — only the labels differ.
  const inflow = isLiability ? month.creditTotal : month.debitTotal;
  const outflow = isLiability ? month.debitTotal : month.creditTotal;

  return (
    <div className={styles.month}>
      <h3 className={styles.monthTitle}>{formatStatementMonth(month.periodStart)}</h3>
      <dl className={styles.monthRows}>
        <div className={styles.monthRow}>
          <dt>{labels.opening}</dt>
          <dd className={lists.amount}>
            {formatAccountBalance(month.openingBalance, account.accountType)}
          </dd>
        </div>
        <div className={styles.monthRow}>
          <dt>{labels.in}</dt>
          <dd className={lists.amount}>{formatVnd(inflow)}</dd>
        </div>
        <div className={styles.monthRow}>
          <dt>{labels.out}</dt>
          <dd className={lists.amount}>{formatVnd(outflow)}</dd>
        </div>
        <div className={cx(styles.monthRow, styles.monthRowTotal)}>
          <dt>{labels.closing}</dt>
          <dd className={lists.amount}>
            {formatAccountBalance(month.closingBalance, account.accountType)}
          </dd>
        </div>
      </dl>
    </div>
  );
}
